import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SchemaForm } from '@/shared/ui/SchemaForm';
import type { JsonSchema, SchemaWidget, UiSchema } from '@/shared/ui/SchemaForm';
import { getToolUiSchema } from './toolCustomWidgets';
import { splitCustomFields } from './toolConfigValidation';
import { HtmlTemplateWidget } from './widgets/HtmlTemplateWidget';
import { HtmlTemplateManager } from './widgets/HtmlTemplateManager';

interface ToolConfigFormProps {
  toolId: string;
  jsonSchema: Record<string, unknown>;
  formData: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  disabled?: boolean;
  uiSchemaOverride?: Record<string, unknown>;
  /**
   * Control for the `kbSelect` ui:widget. Injected by the consuming page
   * (dependency injection pattern) so this admin slice does NOT cross-feat OUT to
   * `features/knowledge-bases`. When omitted, fields requesting `kbSelect` fall
   * back to the schema form's default control.
   */
  kbSelectWidget?: SchemaWidget;
  /** Control for the `kbMultiSelect` ui:widget. Injected by the consuming page. */
  kbMultiSelectWidget?: SchemaWidget;
}

export function ToolConfigForm({
  toolId,
  jsonSchema,
  formData,
  onChange,
  disabled,
  uiSchemaOverride,
  kbSelectWidget,
  kbMultiSelectWidget,
}: ToolConfigFormProps) {
  const { t } = useTranslation('admin');

  const uiSchema = (uiSchemaOverride as UiSchema | undefined) ?? getToolUiSchema(toolId);

  // The same split feeds `isToolConfigValid`, which has to judge exactly the schema the person
  // was shown, so it stays in one place rather than being repeated here.
  const { renderedSchema, customFields } = useMemo(() => {
    const { rjsfSchema, customFields: custom } = splitCustomFields(jsonSchema);
    return { renderedSchema: rjsfSchema as JsonSchema, customFields: custom };
  }, [jsonSchema]);

  const widgets = useMemo<Record<string, SchemaWidget>>(
    () => ({
      ...(kbSelectWidget ? { kbSelect: kbSelectWidget } : {}),
      ...(kbMultiSelectWidget ? { kbMultiSelect: kbMultiSelectWidget } : {}),
      htmlTemplateEditor: HtmlTemplateWidget,
    }),
    [kbSelectWidget, kbMultiSelectWidget],
  );

  const hasTemplates = customFields.includes('templates');
  const hasSchemaFields = Object.keys(renderedSchema.properties ?? {}).length > 0;
  const templates = (formData.templates as Record<string, string> | undefined) ?? {};

  const templateManager = (
    <HtmlTemplateManager
      templates={templates}
      onChange={(next) => onChange({ ...formData, templates: next })}
      disabled={disabled}
    />
  );

  // When templates is the only field, the dedicated manager IS the form.
  if (hasTemplates && !hasSchemaFields) return templateManager;

  return (
    <div className="flex flex-col gap-6">
      {hasSchemaFields && (
        <SchemaForm
          schema={renderedSchema}
          uiSchema={uiSchema}
          formData={formData}
          onChange={onChange}
          disabled={disabled}
          widgets={widgets}
          idPrefix={`tool-config-${toolId}`}
        />
      )}

      {hasTemplates && (
        <div className="flex flex-col gap-2">
          <p className="text-base font-medium text-foreground">
            {t('admin.toolConfigForm.emailTemplates')}
          </p>
          {templateManager}
        </div>
      )}
    </div>
  );
}
